import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Section, Eyebrow, Headline, Lead } from '../../components/iss/IssPrimitives';
import InitiationShell from './InitiationShell';
import './PocTypePage.scss';
import apiService from '../../service/ApiService';

// Union type = only these two strings are allowed
type PocType = 'EXTERNAL_SUPPLIER' | 'INTERNAL_DEVELOPMENT';

const PocTypePage: React.FC = () => {
  // useState remembers the user's choice between re-renders
  // null = nothing selected yet
  const [selected, setSelected] = useState<PocType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  return (
    <InitiationShell activeStep="step1">
      <Section theme="light" spacing="tight">
          <Eyebrow>POC Initiation</Eyebrow>
          <Headline as="h1" size="3xl">
            What type of Proof of Concept do you aim to start?
          </Headline>
          <Lead>
            Step 1 — Please choose what type of POC you would like to initiate.
          </Lead>

          <div className="poc-type">
            {/* External — clickable */}
            <button
              type="button"
              className={
                'poc-type__option' +
                (selected === 'EXTERNAL_SUPPLIER' ? ' poc-type__option--selected' : '')
              }
              onClick={() => setSelected('EXTERNAL_SUPPLIER')}
            >
              <strong>POC with External Supplier</strong>
              <p>
                Collaborating with a 3rd party provider to explore and test their
                solutions within the company&apos;s ecosystem. Focus is on capabilities
                not available in-house.
              </p>
            </button>

            {/* Internal — visible but not clickable */}
            <button
              type="button"
              className="poc-type__option poc-type__option--disabled"
              disabled
              aria-disabled="true"
            >
              <strong>POC as Internal Development</strong>
              <p>
                Leveraging our own internal teams and resources to develop and test
                a new solution within ISS existing capabilities.
              </p>
            </button>
          </div>

          {error ? <p className="bj-field__error">{error}</p> : null}

          <div className="initiation__actions">
            <Link to="/" className="initiation__link-back">
              ← Back to home
            </Link>

            {/* Next only useful once External is selected */}
            <button
              type="button"
              className="initiation__next"
              disabled={selected !== 'EXTERNAL_SUPPLIER' || isSubmitting}
              onClick={async () => {
                if (!selected) return;
                setError(null);
                setIsSubmitting(true);
                try {
                  const poc = await apiService.createPoc({
                    title: 'New PoC draft',
                    pocType: selected,
                    supplierName: undefined,
                  });
                  navigate('/pocs/new/business-justification', {
                    state: {
                      pocId: poc.id,
                      pocType: selected,
                    },
                  });
                } catch (err) {
                  console.error(err);
                  setError(
                    'Could not create PoC. Is the backend running on port 8080?'
                  );
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              {isSubmitting ? 'Creating…' : 'Next →'}
            </button>
          </div>
        </Section>
    </InitiationShell>
  );
};

export default PocTypePage;